<template>
    <div>
        <Sortable
            :list="availableFiles"
            item-key="_tempId"
            tag="div"
            :group="null"
            class="forms__field_input--file__previews-list"
            @end="sorted"
        >
            <template #item="{ element }">
                <transition name="fade" @before-leave="handleLeave">
                    <div v-if="!element.removing" class="flex">
                        <div
                            v-if="props.orderable"
                            class="flex items-center justify-center grow-0 w-10 cursor-grab bg-neutral-100 dark:bg-neutral-950"
                        >
                            <icon
                                icon="hero/solid/ellipsis-vertical"
                                class="size-4 text-gray-400"
                            ></icon>
                        </div>
                        <div
                            v-if="props.isFeatured"
                            class="flex items-center justify-center grow-0 w-10 border-r bg-neutral-50 border-neutral-200 dark:bg-neutral-900 dark:border-neutral-950"
                        >
                            <AdminRadio
                                v-model="featured"
                                :value="element._tempId"
                                :checked="element._tempId === featured"
                            >
                            </AdminRadio>
                        </div>
                        <div class="flex-auto select-none">
                            <file-preview
                                :file="element"
                                :interactive="props.interactive"
                                :size="props.size"
                                :actions="props.actions"
                                :params="props.params"
                                :upload-progress="props.uploadProgress"
                                @open="open"
                                @remove="remove(element)"
                            />
                        </div>
                    </div>
                </transition>
            </template>
        </Sortable>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import type { PreviewFile } from '@src/Fields/Pages/Files/types';
import type { FieldAction } from '@src/Common/Components/types';
import FilePreview from '@src/Fields/Pages/Files/FilePreview.vue';
import { ComponentSize } from '@src/Common/DataGrid/types';
import Sortable from '@src/Common/Components/Sortable.vue';
import { arrayMove } from '@src/Common/Helpers/array';

const props = withDefaults(
    defineProps<{
        files?: PreviewFile[];
        interactive?: boolean;
        size?: ComponentSize;
        orderable?: boolean;
        isFeatured?: boolean;
        actions?: FieldAction[];
        params?: any;
        uploadProgress?: Map<File, number>;
    }>(),
    {
        files: () => [],
        interactive: true,
        size: 'base',
        orderable: false,
        isFeatured: false,
        actions: () => [],
        params: () => ({}),
        uploadProgress: undefined,
    },
);

const emit = defineEmits<{
    (event: 'update:setFeatured', content: number): void;
    (event: 'update:modelValue', content: PreviewFile[]): void;
    (event: 'remove', content: PreviewFile): void;
    (event: 'open', content: PreviewFile): void;
}>();

// Add temporary ID to files (drop nullish entries / sparse-array holes)
const files = ref(
    (props.files ?? [])
        .filter((file) => file != null)
        .map((file) => ({
            ...file,
            _tempId: file && file._tempId ? file._tempId : uuidv4(),
        })),
);

// Only show files that are not being removed in the sortable list
const availableFiles = computed(() => files.value.filter((file) => !file.removing));

const featured = ref<string | null>(null);

const updateFeaturedFromFiles = () => {
    const featuredFile = files.value.find((file) => file?.is_featured);
    if (featuredFile) {
        featured.value = featuredFile._tempId;
        emit(
            'update:setFeatured',
            files.value.findIndex((file) => file._tempId === featured.value),
        );
    }
};

updateFeaturedFromFiles();

const remove = (file: PreviewFile) => {
    file.removing = true;
    setTimeout(() => {
        emit('remove', file);
    }, 300);
};

const handleLeave = (el: HTMLElement) => {
    el.style.height = `${el.offsetHeight}px`;
};

const sorted = (event: any) => {
    // Work with availableFiles (non-removing files) and map back to full files array
    const availableFilesArray = [...availableFiles.value];
    const sortedAvailableFiles = arrayMove(availableFilesArray, event.oldIndex, event.newIndex);

    // Rebuild the full files array preserving removing files in their original positions
    const newFiles = [...files.value];
    let availableIndex = 0;

    for (let i = 0; i < newFiles.length; i++) {
        if (!newFiles[i].removing) {
            newFiles[i] = sortedAvailableFiles[availableIndex];
            availableIndex++;
        }
    }

    files.value = newFiles;

    // If featured file is moved, update featured index
    if (featured.value) {
        emit(
            'update:setFeatured',
            files.value.findIndex((file) => file._tempId === featured.value),
        );
    }

    /// Update model value
    /* eslint-disable @typescript-eslint/no-unused-vars */
    emit(
        'update:modelValue',
        files.value.map(({ _tempId, ...file }) => file),
    );
    /* eslint-enable @typescript-eslint/no-unused-vars */
};

const open = (file: PreviewFile) => {
    emit('open', file);
};

// Watch for changes in files and update internal files
watch(
    () => props.files,
    (newFiles) => {
        files.value = (newFiles ?? [])
            .filter((file) => file != null)
            .map((file) => ({
                ...file,
                _tempId: file && file._tempId ? file._tempId : uuidv4(),
            }));

        updateFeaturedFromFiles();
    },
    { immediate: true },
);

// Watch for changes in featured and update the model value
watch(
    () => featured.value,
    (newId) => {
        const featuredIndex = files.value.findIndex((file) => file._tempId === newId);
        if (featuredIndex !== -1) {
            emit('update:setFeatured', featuredIndex);
        }
    },
);
</script>

<style scoped>
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-leave-to {
    opacity: 0;
}
</style>
